// This file is used in the browser,
// so it must only import types (ts and zod) to not trigger a trpc server error due to bad tree shaking.
import { z } from 'zod/v4-mini';
import { ZodTaskStats } from '../types/TaskStats.js';
import { ZodTaskList } from '../types/TaskList.js';
import { createDataPath } from '@ms-cloudpack/data-bus';
import { ZodNotifyLinkChangeInput } from '../types/NotifyLinkChangeInput.js';
import { ZodLinkPathInput, ZodLinkPathOutput } from '../types/LinkPath.js';
import { ZodGetLinkConfigOutput } from '../types/GetLinkConfigOutput.js';
import { ZodPackageUpdatePayload } from '../types/PackageUpdatePayload.js';
/**
 * Used for tracking the reload count.
 */
export const reloadCountSource = createDataPath({
    path: ['session', 'version'],
    type: z.number(),
});
/**
 * Used for tracking the task stats displayed in the badge.
 */
export const taskStatsSource = createDataPath({
    path: ['task', 'stats'],
    type: ZodTaskStats,
});
/**
 * Used for tracking the list of tasks.
 */
export const taskListSource = createDataPath({
    path: ['task', 'list'],
    type: ZodTaskList,
});
/**
 * Used for tracking entry errors: `Failed to resolve module specifier "${packageName}@${version}${filePath}".`
 * This is used to display errors in the UI and to notify the user of the error when autoUpdateEntries is enabled.
 */
export const errorEntrySource = createDataPath({
    path: ['error', 'entry'],
    type: z.string(),
});
/**
 * Used for tracking link updates.
 */
export const linkUpdateSource = createDataPath({
    path: ['link', 'update'],
    type: ZodNotifyLinkChangeInput,
});
/**
 * Used for fetching the host configuration.
 */
export const linkConfigSource = createDataPath({
    path: ['link', 'config'],
    type: ZodGetLinkConfigOutput,
});
/**
 * Used for tracking linked paths.
 */
export const linkPathsSource = createDataPath({
    path: ['link', 'paths'],
    type: ZodLinkPathInput,
});
/**
 * Used for tracking when linking is done.
 */
export const linkDoneSource = createDataPath({
    path: ['link', 'done'],
    type: ZodLinkPathOutput,
});
/**
 * Used for detecting host disconnection on link.
 */
export const linkDisconnectedSource = createDataPath({
    path: ['link', 'disconnected'],
    type: z.object({ reason: z.literal(['lost', 'unreachable']) }),
});
/**
 * Used for HMR - notifies browser when a package has been rebuilt.
 * The bundleUrl in each entry point already contains the updated hash.
 */
export const packageUpdateSource = createDataPath({
    path: ['package', 'update'],
    type: ZodPackageUpdatePayload,
});
//# sourceMappingURL=busSources.js.map